/**
 * Utility functions for path-based checkbox state management
 *
 * This module consolidates all path building, parsing, and manipulation logic
 * that was previously scattered across multiple files.
 */

import { AssessmentTab } from "../types";

export interface CheckboxPath {
  tabKey: string;
  groupId?: string;
  layerId?: string;
  sectionId?: string;
}

/**
 * Get parent path from a dot-notation path
 * @param path - The path to get parent for (e.g., "demographics.age")
 * @returns Parent path or null if no parent (e.g., "demographics" or null)
 */
export function getParentPath(path: string): string | null {
  const lastDotIndex = path.lastIndexOf(".");
  return lastDotIndex > 0 ? path.substring(0, lastDotIndex) : null;
}

/**
 * Get child paths for a given parent path
 * @param parentPath - The parent path (e.g., "demographics")
 * @param allPaths - All available paths
 * @returns Array of direct child paths
 */
export function getChildPaths(parentPath: string, allPaths: string[]): string[] {
  const prefix = `${parentPath}.`;
  return allPaths.filter((path) => path.startsWith(prefix) && !path.substring(prefix.length).includes("."));
}

/**
 * Calculate node state based on children states and hidden IDs
 * @param nodePath - The node path to calculate state for
 * @param hiddenIds - Array of hidden/unchecked IDs
 * @param allPaths - All available paths
 * @returns boolean for checked/unchecked or "indeterminate" for mixed state
 */
export function calculateNodeState(
  nodePath: string,
  hiddenIds: string[],
  allPaths: string[]
): boolean | "indeterminate" {
  const childPaths = getChildPaths(nodePath, allPaths);

  if (childPaths.length === 0) {
    return !hiddenIds.includes(nodePath);
  }

  const hiddenChildren = childPaths.filter((path) => hiddenIds.includes(path));

  if (hiddenChildren.length === 0) return true;
  if (hiddenChildren.length === childPaths.length) return false;
  return "indeterminate";
}

/**
 * Get all descendant paths for a given parent path
 * @param parentPath - The parent path
 * @param allPaths - All available paths
 * @returns Array of all descendant paths
 */
export function getDescendantPaths(parentPath: string, allPaths: string[]): string[] {
  const prefix = `${parentPath}.`;
  return allPaths.filter((path) => path.startsWith(prefix));
}

/**
 * Builds a dot-separated path string from a CheckboxPath object.
 *
 * This function handles two path formats:
 * - Layer paths: "tabKey.layerId"
 * - Section paths: "tabKey.groupId.sectionId"
 *
 * @param path - Object containing path components
 * @returns Dot-separated path string
 * @throws Error if neither layerId nor groupId+sectionId are provided
 */
export function buildCheckboxPath(path: CheckboxPath): string {
  const { tabKey, groupId, layerId, sectionId } = path;

  if (sectionId && groupId) {
    return `${tabKey}.${groupId}.${sectionId}`;
  }

  if (layerId) {
    return `${tabKey}.${layerId}`;
  }

  throw new Error("Invalid checkbox path: must provide either layerId or both groupId and sectionId");
}

/**
 * Parses a dot-separated path string into a CheckboxPath object.
 *
 * This is the inverse of buildCheckboxPath, used to convert stored
 * path strings back into structured objects for manipulation.
 *
 * Supported formats:
 * - "tabKey.layerId" -> Layer path
 * - "tabKey.groupId.sectionId" -> Section path
 *
 * @param pathString - Dot-separated path string
 * @returns Parsed CheckboxPath object
 * @throws Error if the path format is invalid
 */
export function parseCheckboxPath(pathString: string): CheckboxPath {
  const parts = pathString.split(".");

  if (parts.length === 2) {
    return {
      tabKey: parts[0],
      layerId: parts[1],
    };
  }

  if (parts.length === 3) {
    return {
      tabKey: parts[0],
      groupId: parts[1],
      sectionId: parts[2],
    };
  }

  throw new Error(`Invalid checkbox path format: ${pathString}`);
}

/**
 * Builds a layer path for basic layer-based structures.
 *
 * @param tabKey - The tab key
 * @param layerId - The layer ID
 * @returns Path string in format "tabKey.layerId"
 */
export function buildLayerPath(tabKey: string, layerId: string): string {
  return `${tabKey}.${layerId}`;
}

/**
 * Builds a section path for section-based structures.
 *
 * @param tabKey - The tab key
 * @param groupId - The group ID
 * @param sectionId - The section ID
 * @returns Path string in format "tabKey.groupId.sectionId"
 */
export function buildSectionPath(tabKey: string, groupId: string, sectionId: string): string {
  return `${tabKey}.${groupId}.${sectionId}`;
}

/**
 * Builds a group path.
 *
 * @param tabKey - The tab key
 * @param groupId - The group ID
 * @returns Path string in format "tabKey.groupId"
 */
export function buildGroupPath(tabKey: string, groupId: string): string {
  return `${tabKey}.${groupId}`;
}

/**
 * Validates if a path string follows the expected format.
 *
 * @param pathString - The path string to validate
 * @returns True if the path is valid
 */
export function isValidPath(pathString: string): boolean {
  try {
    parseCheckboxPath(pathString);
    return true;
  } catch {
    return false;
  }
}

/**
 * Builds all paths for assessment tabs using basic layer-based logic.
 *
 * @param assessmentTabs - Array of assessment tabs
 * @returns Array of all layer paths
 */
export function buildLayerPaths(assessmentTabs: AssessmentTab[]): string[] {
  const paths: string[] = [];

  assessmentTabs.forEach((tab) => {
    tab.groups.forEach((group) => {
      group.layerIds.forEach((layerId) => {
        paths.push(buildLayerPath(tab.key, layerId));
      });
    });
  });

  return paths;
}

/**
 * Builds all paths for assessment tabs using section-based logic.
 *
 * @param assessmentTabs - Array of assessment tabs
 * @returns Array of all section/layer paths
 */
export function buildSectionPaths(assessmentTabs: AssessmentTab[]): string[] {
  const paths: string[] = [];

  assessmentTabs.forEach((tab) => {
    tab.groups.forEach((group) => {
      if (group.sectionIds) {
        group.sectionIds.forEach((sectionId) => {
          paths.push(buildSectionPath(tab.key, group.id, sectionId));
        });
      } else {
        group.layerIds.forEach((layerId) => {
          paths.push(buildLayerPath(tab.key, layerId));
        });
      }
    });
  });

  return paths;
}
